/**
 * Order history.
 *
 * Two things used to live here and no longer do. Placing an order was a POST
 * that invented one from whatever the client sent — an order now only exists
 * downstream of a verified payment, so that lives in api/payments.js. And
 * discount codes were checked against a local promo table — they come from
 * tubstudio's coupon service now, so they live in api/coupons.js.
 */

import { USE_MOCKS } from '@/config';
import { delay, request } from './client';

/** Mock-mode order history. The mock IS the backend here, so it has to remember. */
const MOCK_ORDERS_KEY = 'tub.mockOrders';

function readMockOrders() {
  try {
    return JSON.parse(window.localStorage.getItem(MOCK_ORDERS_KEY) ?? '[]');
  } catch {
    return [];
  }
}

/**
 * Files a mock order into local history.
 *
 * Exported for api/payments.js, which is where a mock order is now completed —
 * the store itself stays here because this is the module that reads it back.
 */
export function rememberMockOrder(order, username) {
  try {
    const saved = readMockOrders();
    // Stored per username so signing in as someone else doesn't show their books.
    saved.unshift({ ...order, username, placed_at: new Date().toISOString() });
    window.localStorage.setItem(MOCK_ORDERS_KEY, JSON.stringify(saved));
  } catch {
    /* storage unavailable — history is a nicety, not worth failing a purchase */
  }
}

/**
 * Everything the signed-in user has bought, newest first.
 *
 * Takes no user argument on purpose: the backend scopes this to whoever the
 * session token belongs to, so there is no way to ask for anyone else's.
 */
export async function listOrders({ signal } = {}) {
  if (USE_MOCKS) {
    await delay(200);
    return readMockOrders();
  }
  return request('/api/orders', { signal });
}
